import { useState, useCallback } from 'react'
import { useExtensionsQuery, databaseKeys } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { AgentProxyService } from '@/services/agentProxyService'
import { useToast } from '@/hooks/useToast'
import { useQueryClient } from '@tanstack/react-query'
import { CreateExtensionView } from './CreateExtensionView'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
// Define local interface for Extension
interface Extension {
  name: string
  version?: string
  schema?: string
  owner?: string
  relocatable?: boolean
  description?: string
}
import {
  PuzzlePieceIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  PlusCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline'

interface ExtensionsViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onExtensionSelect?: (extensionName: string) => void
}

export function ExtensionsView({ connection, database, schema, onExtensionSelect }: ExtensionsViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isDroppingExtension, setIsDroppingExtension] = useState<string | null>(null)
  const [confirmDropExtension, setConfirmDropExtension] = useState<{ name: string; cascade: boolean } | null>(null)
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const { data: extensionsResponse, isLoading, error, refetch } = useExtensionsQuery(
    connection.id,
    database,
    true, // isPostgreSQL
    { enabled: true }
  )

  const allExtensions = Array.isArray(extensionsResponse) ? extensionsResponse : []

  const filteredAndSortedExtensions = allExtensions
    .filter((extension: Extension) => {
      if (!searchTerm) return true
      return extension.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: Extension, b: Extension) => {
      const aValue = a.name || ''
      const bValue = b.name || ''
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  const getSortIcon = () => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  const handleDropExtension = useCallback(async (extensionName: string, cascade = false) => {
    setIsDroppingExtension(extensionName)
    try {
      await AgentProxyService.dropExtension(
        connection.id,
        database,
        extensionName,
        cascade
      )
      
      showToast('success', `Extension "${extensionName}" dropped successfully`)
      await refetch()
      await queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'extensions', connection.id, database] })
    } catch (error) {
      console.error('Failed to drop extension:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to drop extension'
      showToast('error', errorMessage)
    } finally {
      setIsDroppingExtension(null)
    }
  }, [connection.id, database, showToast, refetch, queryClient])

  const handleExtensionCreated = useCallback(() => {
    setShowCreateModal(false)
    void (async () => {
      await refetch()
      await queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'extensions', connection.id, database] })
    })()
  }, [refetch, queryClient, connection.id, database])

  const createDropHandler = useCallback((extensionName: string) => {
    return () => { setConfirmDropExtension({ name: extensionName, cascade: false }) }
  }, [])

  const handleConfirmDrop = useCallback(() => {
    if (confirmDropExtension) {
      void handleDropExtension(confirmDropExtension.name, confirmDropExtension.cascade)
      setConfirmDropExtension(null)
    }
  }, [confirmDropExtension, handleDropExtension])

  const handleCloseConfirmation = useCallback(() => {
    setConfirmDropExtension(null)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading extensions: {error.message}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <PuzzlePieceIcon className="w-6 h-6 mr-2 text-green-600" />
              Extensions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedExtensions.length} extensions
            </p>
          </div>
          <button type="button"
            onClick={() => { setShowCreateModal(true) }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Install Extension
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search extensions by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-6 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleSort}
              >
                Extension Name
                {getSortIcon()}
              </div>
              <div className="col-span-2">Version</div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedExtensions.length === 0 ? (
              <div className="text-center py-12">
                <PuzzlePieceIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  No extensions found
                </p>
              </div>
            ) : (
              filteredAndSortedExtensions.map((extension: Extension) => (
                <div key={extension.name} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="col-span-6 flex items-center">
                    <PuzzlePieceIcon className="w-4 h-4 text-green-600" />
                    <div className="ml-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {extension.name}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center text-sm text-gray-600 dark:text-gray-400">
                    {extension.version || 'N/A'}
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      onClick={() => { onExtensionSelect?.(extension.name) }}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="View details"
                    >
                      <InformationCircleIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    <button type="button"
                      onClick={createDropHandler(extension.name)}
                      disabled={isDroppingExtension === extension.name}
                      className="p-1 bg-red-100 hover:bg-red-200 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Drop extension"
                    >
                      {isDroppingExtension === extension.name ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <TrashIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedExtensions.length} of {allExtensions.length} extensions
          </div>
        </div>
      </div>

      {/* Create Extension Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Install Extension
              </h3>
              <button type="button"
                onClick={() => { setShowCreateModal(false) }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
              <CreateExtensionView
                connection={connection}
                database={database}
                onExtensionInstalled={handleExtensionCreated}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmDropExtension}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDrop}
        title="Drop Extension"
        message={confirmDropExtension ? `Are you sure you want to drop the extension "${confirmDropExtension.name}"${confirmDropExtension.cascade ? ' and all dependent objects' : ''}? This action cannot be undone.` : ''}
        confirmText="Drop Extension"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}
